import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import MailIcon from "@/components/SVGComponents/MailIcon";
import { emailRegex } from "@/lib/regex";
import { combineClasses, requiredMessage } from "@/lib/utils";
import toast from "@/lib/toast";
import styles from "./InviteDirectlyModal.module.scss";

interface SelectOption {
  label: string;
  value: string;
}

// No confirmed API for chapters/invite-expiry windows exists yet — see
// the same note on pages/Members/components/AddMemberModal.tsx.
const CHAPTER_OPTIONS: SelectOption[] = [
  { label: "Mumbai", value: "mumbai" },
  { label: "Pune", value: "pune" },
  { label: "Bengaluru", value: "bengaluru" },
  { label: "Delhi NCR", value: "delhi-ncr" },
];

const INVITE_EXPIRES_OPTIONS: SelectOption[] = [
  { label: "7 days", value: "7" },
  { label: "14 days", value: "14" },
  { label: "30 days", value: "30" },
];

type Persona = "founder" | "investor";

interface InviteDirectlyFormValues {
  fullName: string;
  email: string;
  persona: Persona;
  chapter: SelectOption | null;
  inviteExpires: SelectOption | null;
  personalNote: string;
}

// Full Name + Email — rendered as a 2-column grid.
const TOP_GRID_FIELDS: FormConfig = {
  fullName: {
    controlType: "input",
    label: "Full name",
    labelVariant: "caption",
    placeholder: "e.g. Aarav Mehta",
    validations: {
      required: requiredMessage("Full name"),
      maxLength: { value: 100, message: "Full name must be under 100 characters" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
  email: {
    controlType: "input",
    label: "Email",
    labelVariant: "caption",
    placeholder: "name@company.com",
    validations: {
      required: requiredMessage("Email"),
      pattern: { value: emailRegex, message: "Enter a valid email" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
};

// Chapter + Invite expires — its own grid, since Persona sits between the
// two grids and formRenderer has no layout model of its own.
const BOTTOM_GRID_FIELDS: FormConfig = {
  chapter: {
    controlType: "select",
    label: "Chapter",
    labelVariant: "caption",
    isControlled: true,
    validations: { required: requiredMessage("Chapter") },
    props: { options: CHAPTER_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
  },
  inviteExpires: {
    controlType: "select",
    label: "Invite expires",
    labelVariant: "caption",
    isControlled: true,
    props: { options: INVITE_EXPIRES_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
  },
};

const NOTE_FIELD: FormConfig = {
  personalNote: {
    controlType: "textarea",
    label: "Personal note (optional)",
    labelVariant: "caption",
    placeholder: "Appears above the application form.",
    validations: { maxLength: { value: 500, message: "Personal note must be under 500 characters" } },
    props: { className: combineClasses(styles.fieldTextarea) },
  },
};

const PERSONA_OPTIONS: { value: Persona; title: string; description: string }[] = [
  { value: "founder", title: "Founder", description: "Routes to the Founder questionnaire" },
  { value: "investor", title: "Investor", description: "Routes to the Investor questionnaire" },
];

export interface InviteDirectlyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteDirectlyModal = ({ isOpen, onClose }: InviteDirectlyModalProps) => {
  const { register, handleSubmit, control, reset, watch, formState } = useForm<InviteDirectlyFormValues>({
    defaultValues: {
      fullName: "",
      email: "",
      persona: "founder",
      chapter: CHAPTER_OPTIONS[0] ?? null,
      inviteExpires: INVITE_EXPIRES_OPTIONS[1] ?? null,
      personalNote: "",
    },
  });

  const persona = watch("persona");

  const handleClose = () => {
    reset();
    onClose();
  };

  // No "send invite" endpoint is confirmed to exist yet — same UI-only
  // approach as AddMemberModal (docs/decisions.md #23) until one is.
  const onSubmit = handleSubmit(() => {
    toast.warning("This form isn't connected to a real API yet — no invitation was sent.");
    handleClose();
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite directly"
      subtitle="Skips the waitlist and sends a membership invitation."
      onSubmit={onSubmit}
      submitLabel="Send invitation"
      submitIcon={<MailIcon />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.inviteModal)}
    >
      <div className={styles.fieldGrid}>
        {formRenderer<InviteDirectlyFormValues>(TOP_GRID_FIELDS, formState, register, control)}
      </div>

      <section className={styles.section}>
        <p className={styles.sectionLabel}>Persona</p>
        <div className={styles.optionList}>
          {PERSONA_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={combineClasses(styles.optionCard, persona === option.value && styles.optionCardSelected)}
            >
              <input
                type="radio"
                value={option.value}
                {...register("persona")}
                disabled={formState.isSubmitting}
                className={styles.optionRadio}
              />
              <span className={styles.optionText}>
                <span className={styles.optionTitle}>{option.title}</span>
                <span className={styles.optionDescription}>{option.description}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <div className={styles.fieldGrid}>
        {formRenderer<InviteDirectlyFormValues>(BOTTOM_GRID_FIELDS, formState, register, control)}
      </div>

      {formRenderer<InviteDirectlyFormValues>(NOTE_FIELD, formState, register, control)}
    </FormModal>
  );
};

export default InviteDirectlyModal;
